/**
 * Migration dependency graph.
 *
 * The site-migration tool copies documents collection-by-collection, filtered by SiteId.
 * Most `ISiteEntity` models hold foreign references to other models (a Pen points at a
 * Building, a PregnancyEvent points at a StemAnimal, ...). If we migrate a model without the
 * data it references, those references dangle in the target environment.
 *
 * This map records, for every migratable model, the other models it references directly
 * ("SOURCE depends on TARGET"). The keys/targets are the C# type names returned by
 * `AdminFeaturesController.GetSubclassesOfSiteEnity` — i.e. the same strings used as the
 * `SkioldModels.Values.*` / `SkioldModels.Description.*` i18n keys.
 *
 * `soft: true` marks references that are not a hard MongoId foreign key — number-keyed
 * references (e.g. a Distriwin valve *number* rather than the Valve document id), natural-key
 * lookups, or provenance pointers (a young animal's originating farrowing). They cannot leave a
 * dangling ObjectId, but the referenced data may still be missing, so they are treated as
 * prerequisites for auto-selection just like hard edges — the `soft` flag is retained only so
 * the UI can explain *why* something was pulled in.
 *
 * Derived by analysing the DAL models under `api/Skiold.API/DAL/Models`.
 */

export interface MigrationDependencyEdge {
	/** The model that must also be migrated. */
	target: string;
	/** The field / relationship that establishes the reference (for tooltips + traceability). */
	via: string;
	/** True when the reference is number/natural-key/provenance based rather than a hard MongoId FK. */
	soft: boolean;
}

const hard = (target: string, via: string): MigrationDependencyEdge => ({ target, via, soft: false });
const soft = (target: string, via: string): MigrationDependencyEdge => ({ target, via, soft: true });

export const migrationDependencies: Record<string, MigrationDependencyEdge[]> = {
	// --- Animals, pregnancies, movements ---
	StemAnimal: [hard('Pen', 'EntrancePenId'), hard('Reason', 'DepartureReasonId'), soft('PregnancyEvent', 'FromPregnancyId'), soft('EsfFeedCurve', 'EntranceFeedCurveNumber')],
	PregnancyEvent: [hard('StemAnimal', 'StemAnimalId / BoarId')],
	DeadPigletsEvent: [hard('PregnancyEvent', 'PregnancyId'), hard('StemAnimal', 'StemAnimalId / YoungAnimalId'), hard('Reason', 'ReasonId')],
	MoveEvent: [hard('StemAnimal', 'StemAnimalId'), hard('Pen', 'PenId'), soft('EsfFeedCurve', 'FeedCurveNumber')],
	LardScanningEvent: [hard('StemAnimal', 'StemAnimalId')],
	PoolYoungFemale: [hard('StemAnimal', 'StemAnimalId'), hard('Pen', 'PenId'), hard('Reason', 'DepartureReasonId')],
	UnknownAnimal: [],
	MatingBatch: [],
	MatingBatchSetting: [],

	// --- Locations & equipment ---
	Building: [],
	Section: [hard('Building', 'BuildingId')],
	Pen: [hard('Building', 'BuildingId'), hard('Section', 'SectionId')],
	PenRetentionTime: [hard('Pen', 'PenId')],
	ProcessEquipment: [],
	ProcessEquipmentData: [hard('Building', 'BuildingId'), hard('Section', 'SectionId'), soft('StationGroup', 'UnitData.UnitGroupId'), soft('ValveGroup', 'UnitData.UnitGroupId')],
	UnitToPen: [hard('Pen', 'PenId'), hard('ProcessEquipmentData', 'ProcessEquipmentDataId'), soft('StationGroup', 'UnitGroupId'), soft('ValveGroup', 'UnitGroupId')],
	Station: [hard('StationGroup', 'UnitGroupId')],
	StationGroup: [],
	Valve: [hard('ValveGroup', 'ValveGroupId')],
	ValveGroup: [soft('RecipeEntity', 'RecipeNumber')],
	ValveGroupHistory: [hard('ValveGroup', 'ValveGroupId')],

	// --- Treatments, drugs, work lists, validation ---
	Treatment: [hard('TreatmentPlan', 'TreatmentPlanId'), hard('Building', 'BuildingId'), hard('Section', 'SectionId'), hard('Pen', 'PenId'), hard('UserProfile', 'ExecutedByProfileId')],
	TreatmentPlan: [hard('TreatmentDefinition', 'TreatmentDefinitionId'), hard('StemAnimal', 'StemAnimalId')],
	TreatmentDefinition: [hard('Diagnose', 'DiagnoseId'), hard('DrugType', 'SubTreatment.DrugTypeId')],
	Drug: [hard('DrugType', 'DrugTypeId')],
	AmountBase: [hard('Drug', 'DrugId'), hard('Treatment', 'Usage.TreatmentId'), hard('UserProfile', 'RegisteredById')],
	WorkListSetting: [hard('TreatmentDefinition', 'TreatmentDefinitionId'), hard('Building', 'BuildingId'), hard('Section', 'SectionId'), hard('Pen', 'PenId'), hard('VaccinationDefinition', 'VaccineSubTask.VaccineDefinitionId')],
	WorkListToTreatmentPlan: [hard('TreatmentPlan', 'TreatmentPlanId'), hard('WorkListSetting', 'WorkListSettingId'), soft('Diagnose', 'DiagnoseIdentifier')],
	ValidationSetup: [hard('Pen', 'WeanedPenId')],

	// --- Feeding, ESF, recipes, growth pigs ---
	Feeding: [hard('FeedingSubject', 'FeedingSubjectId'), soft('Building', 'FeedingUsage.BuildingId'), soft('Section', 'FeedingUsage.SectionId'), soft('RecipeEntity', 'FeedingUsage.RecipeId')],
	FeedingSubject: [hard('FeedingCategory', 'FeedingCategoryId')],
	RecipeEntity: [],
	EsfDailyStatus: [hard('StemAnimal', 'StemAnimalId')],
	EsfFeedCurve: [],
	EsfFeedingStatus: [hard('StemAnimal', 'StemAnimalId')],
	GrowthPigsEvent: [hard('Building', 'To/FromBuildingId'), hard('Section', 'To/FromSectionId'), hard('Pen', 'To/FromPenId'), hard('PoolYoungFemale', 'PoolYoungFemaleId'), hard('Reason', 'DepartureReasonId')],
	GrowthPigTuneProduction: [hard('Building', 'BuildingId'), hard('Section', 'SectionId'), hard('Pen', 'PenId')],

	// --- Distriwin, Nucleus, Cloud-to-device, Slaughter ---
	DistriwinFeedComponent: [hard('Feeding', 'FeedingId')],
	DistriwinFeedConsumption: [hard('DistriwinFeedComponent', 'ComponentId'), hard('Pen', 'PenId'), hard('Section', 'SectionId'), hard('Building', 'BuildingId'), soft('Valve', 'ValveId (number)'), soft('RecipeEntity', 'RecipeNumber')],
	DistriwinFeedRegistration: [],
	DistriwinTask: [],
	DistriwinTaskAlert: [soft('DistriwinTask', 'ControllerNumber + TaskNumber')],
	DistriwinValveAdjustments: [hard('Pen', 'PenIds[].PenId')],
	DistriwinOption: [],
	NucleusManagementArchiveFile: [hard('NucleusManagementConnection', 'ConnectionId')],
	NucleusManagementBreedingSettings: [],
	NucleusManagementConnection: [],
	NucleusCommunicationData: [hard('StemAnimal', 'StemAnimalId'), soft('PregnancyEvent', 'NucleusEventData[].PregnancyId')],
	CloudToDeviceMessage: [hard('StemAnimal', 'AnimalId'), soft('CloudToDeviceSequenceNumbers', '(AnimalId, Code) pairing')],
	CloudToDeviceSequenceNumbers: [hard('StemAnimal', 'AnimalId')],
	SlaughterData: [hard('Building', 'BuildingId'), hard('Section', 'SectionId'), soft('SupplierDetail', 'Supplier (number)')],
	SupplierDetail: [hard('Building', 'BuildingId'), hard('Section', 'SectionId'), hard('SlaughterhouseConnection', 'SlaughterHouseConnectionId')],
	SlaughterhouseConnection: [],

	// --- Settings & users ---
	AnalysisSetting: [],
	AnimalReportSetting: [],
	DashboardSettings: [hard('MatingBatch', 'Start/EndMatingBatchId'), hard('Building', 'GpeDashboardSettingItems[].BuildingId')],
	ProductionReportSetting: [],
	GeneralSettings: [hard('Pen', 'FarrowingYoungAnimalsPenId / WeanedPenId'), hard('Section', 'FarrowingYoungAnimalsSectionId / WeanedSectionId')],
	ReasonSettings: [hard('Reason', 'ReasonId')],
	UserProfile: [],
	UserSite: [hard('UserProfile', 'UserId')],

	// --- Global / reference data (not ISiteEntity). Copied whole-collection unless the model
	// carries its own SiteId (VaccinationDefinition, NotificationInstance → site-filtered). ---
	// Note: BMPData, Dashboard, Device, Image, SiteFlow and RegistrationError are excluded from
	// migration entirely (server-side ExcludedEntities), so they are not listed here and Diagnose's
	// Image edge is dropped.
	Diagnose: [hard('DiagnoseCategory', 'DiagnoseCategoryId')],
	DiagnoseCategory: [],
	DrugType: [],
	Reason: [],
	Marking: [],
	FeedingCategory: [],
	BreedTableItem: [],
	NotificationDefinition: [],
	VaccinationDefinition: [hard('Diagnose', 'DiagnoseId'), hard('Drug', 'DrugId')],
	NotificationInstance: [hard('NotificationDefinition', 'NotificationDefinitionId')],
	Country: [],
	Enterprise: [],
	FeedingSettings: [],
	Site: [hard('Enterprise', 'EnterpriseId'), hard('Country', 'CountryId')],
	Licence: [],
	Module: [],
};

/**
 * Models that are NOT site-scoped — they have no SiteId, so migrating them copies the ENTIRE
 * collection (all sites) rather than just the selected site's rows. Kept in sync with the server's
 * `HasSiteId` check (VaccinationDefinition / NotificationInstance carry their own SiteId, so they
 * are site-filtered and deliberately excluded here). Used purely to badge these rows in the UI.
 * `Licence` / `Module` are static in-memory constants and migrate as a no-op.
 */
export const GLOBAL_MODELS = new Set<string>([
	'Diagnose',
	'DiagnoseCategory',
	'DrugType',
	'Reason',
	'Marking',
	'FeedingCategory',
	'BreedTableItem',
	'NotificationDefinition',
	'Country',
	'Enterprise',
	'FeedingSettings',
	'Site',
	'Licence',
	'Module',
]);

export interface ResolvedMigrationSelection {
	/** The full set of models to migrate: user picks + every transitive prerequisite. */
	all: string[];
	/** For each auto-included (non-directly-selected) model, the direct picks that require it. */
	requiredBy: Record<string, string[]>;
}

/**
 * Expand a set of user-chosen models into the full set that must be migrated, following the
 * dependency edges transitively (cycle-safe). Also reports, for each auto-included model, which
 * of the user's picks pulled it in — so the UI can explain the selection.
 */
export function resolveMigrationSelection(directSelected: string[]): ResolvedMigrationSelection {
	const directSet = new Set(directSelected);
	const all = new Set<string>(directSelected);
	const requiredBy: Record<string, Set<string>> = {};

	for (const root of directSelected) {
		// Seed `visited` with the root so a dependency cycle back to it terminates cleanly.
		const visited = new Set<string>([root]);
		const stack = (migrationDependencies[root] || []).map(edge => edge.target);

		while (stack.length > 0) {
			const current = stack.pop() as string;
			if (visited.has(current)) {
				continue;
			}
			visited.add(current);
			all.add(current);

			// Only track "required by" for models the user did not pick directly — a direct pick
			// is shown as chosen, not as a dependency.
			if (!directSet.has(current)) {
				if (!requiredBy[current]) {
					requiredBy[current] = new Set<string>();
				}
				requiredBy[current].add(root);
			}

			for (const edge of migrationDependencies[current] || []) {
				stack.push(edge.target);
			}
		}
	}

	const requiredByArrays: Record<string, string[]> = {};
	for (const model of Object.keys(requiredBy)) {
		requiredByArrays[model] = Array.from(requiredBy[model]);
	}

	return { all: Array.from(all), requiredBy: requiredByArrays };
}
